import i18n from '@nextroll/ar-i18n';
import { RequestError } from 'hooks/useRequest';

import { isValidDate } from '@nextroll/ar-date-picker';

import { PropertyValidation } from 'types/roflWorkflowActionProperty';
import { IBaseNodeIssue } from 'types/rofl';

export const COMPONENT_UNMOUNT_ABORT_ERROR_NAME = 'ComponentUnmountAbortError';
export const GENERIC_ABORT_ERROR = 'AbortError';

function getApiFailedToLoadMessage(entityTitle: string) {
    return i18n.gettext(
        'Unable to access your {{entityTitle}} at the moment. Please exit the workflow builder and try again.',
        {
            entityTitle,
        }
    );
}

/* Validation Errors */
export enum ValidationError {
    GENERIC_REQUIRED = 'generic_required',
    UNSELECTED = 'unselected',
    GENERIC_ENTITY_NOT_FOUND = 'generic_entity_not_found',
    ENTITY_NOT_FOUND = 'entity_not_found',
    INVALID_INPUT = 'invalid_input',
    FIELD_MISSING = 'field_missing',
    READ_ONLY_FIELD_FOR_RECORD_OPERATION = 'read_only_field_for_record_operation',
    FIELD_MAPPING_ISSUE = 'fieldmap_issues_found', // indicates field mapping related errors or warnings
}
export type ValidationErrorType = typeof ValidationError[keyof typeof ValidationError];

function getUnselectedMessage(entityTitle: string) {
    return i18n.gettext(
        'Required to select a {{entityTitle}}.',
        {
            entityTitle,
        }
    );
}

function getGenericRequiredMessage(entityTitle: string) {
    return i18n.gettext(
        'This field is required. Please enter a {{entityTitle}}.',
        {
            entityTitle,
        }
    );
}

function getGenericEntityNotFoundMessage(entityTitle: string) {
    return i18n.gettext(
        'No matching result found. Please review the {{entityTitle}} and try again.',
        {
            entityTitle,
        }
    );
}

function getEntityNotFoundMessage(entityTitle: string) {
    return i18n.gettext(
        'The previous {{entityTitle}} cannot be found. Please try another {{entityTitle}}.',
        {
            entityTitle,
        }
    );
}

function getInvalidErrorMessage(entityTitle: string) {
    return i18n.gettext(
        'This field value is invalid. Please enter a {{entityTitle}}.',
        {
            entityTitle,
        }
    );
}

function getFieldNotFoundMessage(entityTitle: string) {
    return i18n.gettext(
        'The {{entityTitle}} field cannot be found and may have been deleted. Please select another field.',
        {
            entityTitle,
        }
    );
}

function getReadOnlyFieldForRecordOperationErrorMesssage(fieldName: string, serviceTitle?: string) {
    return i18n.gettext(
        'The {{fieldName}} field can no longer be updated. Please double-check your field settings in {{serviceTitle}} or select another field.',
        {
            fieldName,
            serviceTitle,
        }
    );
}

type GetValidationErrorOverload = {
    (error: ValidationErrorType, entityTitle: string, serviceTitle?: string,): string;
};
export const getValidationError: GetValidationErrorOverload = (
    error: ValidationErrorType,
    entityTitle: string,
    serviceTitle?: string,
): string => {
    const validationErrorMap = {
        [ValidationError.GENERIC_REQUIRED]: () => getGenericRequiredMessage(entityTitle),
        [ValidationError.UNSELECTED]: () => getUnselectedMessage(entityTitle),
        [ValidationError.ENTITY_NOT_FOUND]: () => getEntityNotFoundMessage(entityTitle),
        [ValidationError.GENERIC_ENTITY_NOT_FOUND]: () => getGenericEntityNotFoundMessage(entityTitle),
        [ValidationError.INVALID_INPUT]: () => getInvalidErrorMessage(entityTitle),
        [ValidationError.FIELD_MISSING]: () => getFieldNotFoundMessage(entityTitle),
        [ValidationError.READ_ONLY_FIELD_FOR_RECORD_OPERATION]:
            () => getReadOnlyFieldForRecordOperationErrorMesssage(entityTitle, serviceTitle),
    } as { [error: string]: () => string };

    // fall back to general error if none exist
    if (!validationErrorMap[error]) {
        return getApiFailedToLoadMessage(entityTitle);
    }
    return validationErrorMap[error]();
};

/* General Issues */
export enum GeneralIssue {
    NO_SEARCH_RESULTS = 'no_search_results',
    NO_DATA = 'no_data',
    API_FAILED_TO_LOAD = 'api_failed_to_load',
}
export type GeneralIssueType = typeof GeneralIssue[keyof typeof GeneralIssue];

function getNoDataMessage(serviceTitle: string, entityTitle: string) {
    return i18n.gettext(
        'No {{serviceTitle}} {{entityTypePlural}} were found. Please check that there are {{entityTypePlural}} created in your connected {{serviceTitle}} account.',
        {
            serviceTitle,
            // TODO: Replace with actual plural
            entityTypePlural: `${entityTitle}s`,
        }
    );
}

type GetGeneralIssueOverload = {
    (issue: GeneralIssueType, entityTitle: string, serviceTitle: string): string;
    (issue: GeneralIssueType, entityTitle: string): string;
};
export const getGeneralIssue: GetGeneralIssueOverload = (
    issue: GeneralIssueType,
    entityTitle: string,
    serviceTitle: string = '',
): string => {
    const genericIssueMap = {
        [GeneralIssue.NO_SEARCH_RESULTS]: () => i18n.gettext('No results found'),
        [GeneralIssue.NO_DATA]: () => getNoDataMessage(serviceTitle, entityTitle),
        [GeneralIssue.API_FAILED_TO_LOAD]: () => getApiFailedToLoadMessage(entityTitle),
    };

    return genericIssueMap[issue]();
};

export const isAbortReasonComponentUnmount = (abortReason?: string) => (
    abortReason === COMPONENT_UNMOUNT_ABORT_ERROR_NAME
);

export const isErrorGenericAborted = (error: Error) => (
    error instanceof DOMException && error?.name === GENERIC_ABORT_ERROR
);

const isValidHttpUrl = (urlValue:string): boolean => {
    let url;
    try {
        url = new URL(urlValue);
    } catch (_) {
        return false;
    }
    return url.protocol === 'http:' || url.protocol === 'https:';
};

const isValidFloat = (value: string): boolean => (
    /^[0-9]*\.[0-9]+$/.test(value)
);

// https://html.spec.whatwg.org/multipage/input.html#valid-e-mail-address
const isValidEmail = (value:string): boolean => (
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(value)
);

export const isValidPropertyValue = (value: any, validationType: PropertyValidation | undefined): boolean => {
    if (!value || !validationType) {
        return false;
    }

    switch (validationType) {
        case PropertyValidation.URL:
            return isValidHttpUrl(value);
        case PropertyValidation.INT:
            return !Number.isNaN(value) && Number.isInteger(Number(value));
        case PropertyValidation.FLOAT:
            return isValidFloat(value);
        case PropertyValidation.EMAIL:
            return isValidEmail(value);
        case PropertyValidation.DATE || PropertyValidation.DATETIME:
            return isValidDate(value);
        default:
            return true;
    }
};

export const getPropertyInputErrorMessage = (requestError: RequestError, entityTitle: string): string => {
    if (requestError?.response?.status === 400) {
        return getValidationError(
            requestError.parsedBody.code, entityTitle
        );
    } else if (requestError?.response?.status === 404) {
        return getValidationError(
            ValidationError.GENERIC_ENTITY_NOT_FOUND, entityTitle
        );
    } else {
        return getGeneralIssue(GeneralIssue.API_FAILED_TO_LOAD, entityTitle);
    }
};

/*
 * generatePropertyErrorKey generates a lookup key from a property error
 */
export const generatePropertyErrorKey = (propertyName: string, recordField?: string): string => {
    if (recordField) {
        return `${propertyName}:${recordField}`;
    } else {
        return propertyName;
    }
};

/**
 * search schema warnings or errors for the target validation warning / error
 */
export const findWarning = (warnings: IBaseNodeIssue[], target: ValidationError) => warnings
    .find(warning => warning?.property_warnings?.find(w => w.error === target));
export const findError = (errors: IBaseNodeIssue[], target: ValidationError) => errors
    .find(error => error?.property_errors?.find(e => e.error === target));
